import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Ban, Search, Edit3 } from "lucide-react";

const TD_STATUS = ["Pending", "In Review", "In Process", "Takedown Completed", "Rejected", "Cancelled"];

export default function AdminTakedown() {
  const [params] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(params.get("status") || "");
  const [editing, setEditing] = useState(null);

  const load = () => {
    const p = {};
    if (q) p.q = q;
    if (status) p.status = status;
    api.get("/admin/takedown", { params: p }).then((r) => setRecords(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 220); return () => clearTimeout(t); }, [q, status]);

  const save = async () => {
    try {
      await api.patch(`/admin/takedown/${editing.id}`, {
        status: editing.status,
        admin_notes: editing.admin_notes,
      });
      toast.success("Updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Administrator</div>
      <h1 className="mt-2 text-4xl font-bold tracking-tight flex items-center gap-3">
        <Ban className="w-8 h-8 text-rose-400" /> Takedown Requests
      </h1>

      <div className="mt-8 grid md:grid-cols-3 gap-3">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input placeholder="Search by TD ID, song, ISRC, UPC, user or phone..."
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="admin-td-search" className="pl-9 bg-zinc-900 border-zinc-800" />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="admin-td-status" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {TD_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-x-auto">
        <table className="w-full min-w-[1100px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="p-4">Request ID</th>
              <th className="p-4">User</th>
              <th className="p-4">Song</th>
              <th className="p-4">UPC / ISRC</th>
              <th className="p-4">Reason</th>
              <th className="p-4">Requested</th>
              <th className="p-4">Status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id} data-testid={`admin-td-row-${r.id}`}
                className={`border-b border-zinc-900 hover:bg-zinc-900/40 ${r.status === "Pending" ? "bg-amber-500/5" : ""}`}>
                <td className="p-4 font-mono text-cyan-400 text-sm">{r.id}</td>
                <td className="p-4 text-sm">
                  <div>{r.user?.full_name}</div>
                  <div className="text-xs text-zinc-500">{r.user?.phone}</div>
                  <div className="text-xs text-zinc-500">{r.user?.email}</div>
                </td>
                <td className="p-4">
                  <div className="font-medium">{r.song_title}</div>
                  <div className="text-xs text-zinc-500">{r.primary_artist_name}</div>
                  <Link to={`/admin/submissions?q=${encodeURIComponent(r.submission_id)}`}
                    className="text-xs text-cyan-400 hover:underline font-mono">{r.submission_id.slice(0, 8)}...</Link>
                </td>
                <td className="p-4 font-mono text-xs text-emerald-400">{r.upc || "—"}<br />{r.isrc || "—"}</td>
                <td className="p-4 text-sm">{r.reason === "Other" ? r.reason_other : r.reason}</td>
                <td className="p-4 text-zinc-500 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td className="p-4"><StatusBadge status={r.status} /></td>
                <td className="p-4 text-right">
                  <Button size="icon" variant="ghost" onClick={() => setEditing({ ...r })}
                    data-testid={`admin-td-edit-${r.id}`}
                    className="h-8 w-8 text-zinc-400 hover:text-violet-400"><Edit3 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
            {records.length === 0 && <tr><td colSpan="8" className="p-12 text-center text-zinc-500">No takedown requests</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-td-edit-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Ban className="w-5 h-5 text-rose-400" /> Manage Takedown</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="p-3 rounded bg-zinc-900 border border-zinc-800 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-zinc-500">Request</span><code className="text-cyan-400">{editing.id}</code></div>
                <div className="flex justify-between"><span className="text-zinc-500">Song</span><span>{editing.song_title}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">User</span><span>{editing.user?.full_name}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Reason</span><span>{editing.reason === "Other" ? editing.reason_other : editing.reason}</span></div>
                {editing.notes && <div className="mt-2 text-xs text-zinc-400"><span className="text-zinc-500 uppercase tracking-wider">Note:</span> {editing.notes}</div>}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Status</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="td-edit-status" className="mt-1.5 bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent>{TD_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Admin Notes</Label>
                <Textarea rows={3} value={editing.admin_notes || ""} onChange={(e) => setEditing({ ...editing, admin_notes: e.target.value })}
                  data-testid="td-edit-notes" className="mt-1.5 bg-zinc-900 border-zinc-800" placeholder="Visible to user" />
              </div>
              {editing.status_history?.length > 0 && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">History</Label>
                  <div className="mt-1.5 space-y-1 max-h-40 overflow-y-auto">
                    {editing.status_history.map((h, i) => (
                      <div key={i} className="p-2 rounded bg-zinc-900 border border-zinc-800 text-xs">
                        <div className="flex justify-between text-zinc-400">
                          <span>{h.from ? `${h.from} → ` : ""}<span className="text-cyan-400">{h.to}</span></span>
                          <span>{new Date(h.at).toLocaleString()}</span>
                        </div>
                        <div className="text-zinc-500 mt-0.5">by {h.by}{h.note ? ` — ${h.note}` : ""}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-zinc-700 hover:bg-zinc-900">Cancel</Button>
            <Button onClick={save} data-testid="td-edit-save" className="bg-gradient-to-r from-cyan-400 to-violet-500 text-zinc-950 font-semibold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
